class BackInStock extends HTMLElement {
  constructor() {
    super();

    this.wrapperElement = this.querySelector('.back-in-stock');
    this.backdropElement = this.querySelector('.back-in-stock__backdrop');
    this.modalElement = this.querySelector('.back-in-stock__modal');
    this.closeElement = this.querySelector('.back-in-stock__close');
    this.backToBrowsing = this.querySelector('.back-to-browsing-btn');
    this.cancelButton = this.querySelector('.back-in-stock__form-cancel');
    this.formElement = this.querySelector('.back-in-stock__form');
    this.formContentElement = this.querySelector(
      '.back-in-stock__form-content'
    );
    this.inputElement = this.querySelector('.back-in-stock__form-input');
    this.submitButton = this.querySelector('.back-in-stock__form-submit');
    this.continueShoppingButton = this.querySelector(
      '.back-in-stock__form-continue-shopping'
    );
    this.errorElement = this.querySelector('#back-in-stock-error-message');
    this.successElement = this.querySelector('#back-in-stock-success-message');
    this.defaultSuccessMessage = this.successElement.innerHTML;

    this.isOpen = false;
    this.lastFocus = undefined;

    /**
     * a        => Public Klaviyo API Key
     * email    => Email address entered by the subscriber
     * variant  => Product's variant ID
     * platform => Platform ID (such as `shopify` or `bigcommerce`)
     */
    this.requestData = {
      a: this.wrapperElement.dataset.klaviyoKey,
      email: undefined,
      variant: undefined,
      platform: 'shopify',
    };
  }

  openModal = (event) => {
    try {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      this.updateHeaderHeight();
      this.classList.remove('hidden');
      this.successElement.classList.add('hidden');
      this.continueShoppingButton.classList.add('hidden');
      this.inputElement.classList.remove('hidden');
      this.submitButton.classList.remove('hidden');
      this.cancelButton.classList.remove('hidden');

      this.wrapperElement.setAttribute('open', '');
      this.wrapperElement.setAttribute('aria-hidden', false);
      this.modalElement.setAttribute('tabindex', 0);
      this.inputElement.focus();

      this.closeElement.addEventListener('click', this.closeModal);
      this.closeElement.addEventListener('ontouchstart', this.closeModal);
      this.backToBrowsing.addEventListener('click', this.closeModal);
      this.backToBrowsing.addEventListener('ontouchstart', this.closeModal);
      this.cancelButton.addEventListener('click', this.closeModal);
      this.cancelButton.addEventListener('ontouchstart', this.closeModal);

      this.backdropElement.addEventListener('click', this.closeModal);
      this.backdropElement.addEventListener('ontouchstart', this.closeModal);

      this.formElement.addEventListener('submit', this.sendForm);

      window.scrollTo(0, 0);
      document.body.classList.add('overflow-hidden');
      document.addEventListener('focus', this.restrictFocus, true);
      document.addEventListener('keydown', this.closeModal);

      this.isOpen = true;
      this.lastFocus = document.activeElement;
    } catch (error) {
      console.error(error);
    }
  };

  closeModal = (event) => {
    try {
      if (event.type === 'click') {
        event.stopPropagation();
        event.preventDefault();
      }

      /**
       * make sure to only do something if it's a click
       * on the closing elements, or after pressing Esc key.
       */
      if (
        event.type === 'click' ||
        (event.type === 'keydown' &&
          (event.key === 'Escape' || event.keyCode === 27))
      ) {
        this.classList.add('hidden');
        this.wrapperElement.removeAttribute('open');
        this.wrapperElement.setAttribute('aria-hidden', true);
        this.modalElement.setAttribute('tabindex', '-1');

        this.closeElement.removeEventListener('click', this.closeModal);
        this.closeElement.removeEventListener('ontouchstart', this.closeModal);
        this.backToBrowsing.removeEventListener('click', this.closeModal);
        this.backToBrowsing.removeEventListener(
          'ontouchstart',
          this.closeModal
        );
        this.cancelButton.removeEventListener('click', this.closeModal);
        this.cancelButton.removeEventListener('ontouchstart', this.closeModal);
        this.continueShoppingButton.removeEventListener(
          'click',
          this.closeModal
        );
        this.continueShoppingButton.removeEventListener(
          'ontouchstart',
          this.closeModal
        );

        this.backdropElement.removeEventListener('click', this.closeModal);
        this.backdropElement.removeEventListener(
          'ontouchstart',
          this.closeModal
        );

        this.formElement.removeEventListener('submit', this.sendForm);

        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('focus', this.restrictFocus, true);
        document.removeEventListener('keydown', this.closeModal);

        this.hideSuccessMessage();

        this.isOpen = false;
        this.lastFocus.focus();
      }
    } catch (error) {
      console.error(error);
    }
  };

  sendForm = (event) => {
    try {
      event.preventDefault();

      const email = this.inputElement.value;

      if (
        !email.match(
          /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
      ) {
        this.showErrorMessage();
        return;
      }

      if (!this.requestData.variant) {
        throw new Error('A variant must be defined.');
      }

      this.hideErrorMessage();

      this.requestData.email = email;

      this.submitButton.setAttribute('disabled', '');
      this.submitButton.classList.add('button--secondary');
      this.submitButton.classList.add('loading');
      this.submitButton.lastElementChild.classList.remove('hidden');

      fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.requestData.a}`,
        {
          method: 'POST',
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: this.requestData.email,
                    },
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${this.requestData.variant}`,
                  },
                },
              },
            },
          }),
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
        }
      )
        .then((response) => {
          if (!response.ok) {
            return response.json().then((res) => {
              const [error] = res.errors;
              if (error.status >= 400) {
                this.showSubmitMessage(error.title, true);
              } else if (error.status >= 500) {
                this.showSubmitMessage(
                  `Something went wrong. Please try again later.`,
                  true
                );
              }
              console.error(error);
            });
          }

          if (window.FarmRio.events.backInStockFormSubscribed)
            document.dispatchEvent(
              new Event(window.FarmRio.events.backInStockFormSubscribed)
            );

          this.formElement.reset();
          this.showSubmitMessage();
          return;
        })
        .catch((e) => {
          console.error(e);
          this.showSubmitMessage(e.message, true);
        })
        .finally(() => {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.classList.remove('button--secondary');
          this.submitButton.classList.remove('loading');
          this.submitButton.lastElementChild.classList.add('hidden');
        });
    } catch (error) {
      console.error(error);
    }
  };

  showSubmitMessage = (
    message = this.defaultSuccessMessage,
    errorLike = false
  ) => {
    try {
      if (message !== this.defaultSuccessMessage) {
        this.successElement.innerHTML = message;
      }

      if (errorLike) {
        this.successElement.classList.add('back-in-stock__message--error');
        this.successElement.classList.remove('back-in-stock__message--success');
      }

      this.successElement.classList.remove('hidden');

      this.continueShoppingButton.classList.remove('hidden');
      this.continueShoppingButton.addEventListener('click', this.closeModal);
      this.continueShoppingButton.addEventListener(
        'ontouchstart',
        this.closeModal
      );

      this.inputElement.classList.add('hidden');
      this.submitButton.classList.add('hidden');
      this.cancelButton.classList.add('hidden');

      this.inputElement.setAttribute(
        'aria-describedby',
        'back-in-stock-success-message'
      );
    } catch (error) {
      console.error(error);
    }
  };

  hideSuccessMessage = () => {
    try {
      this.successElement.classList.add('hidden');

      if (this.successElement.innerHTML !== this.defaultSuccessMessage) {
        this.successElement.innerHTML = this.defaultSuccessMessage;
      }

      if (
        !this.successElement.classList.contains(
          'back-in-stock__message--success'
        )
      ) {
        this.successElement.classList.add('back-in-stock__message--success');
      }

      this.inputElement.removeAttribute('aria-describedby');
    } catch (error) {
      console.error(error);
    }
  };

  showErrorMessage = () => {
    try {
      this.errorElement.classList.remove('hidden');

      this.inputElement.setAttribute('aria-invalid', true);
      this.inputElement.setAttribute(
        'aria-describedby',
        'back-in-stock-error-message'
      );
      this.inputElement.focus();
    } catch (error) {
      console.error(error);
    }
  };

  hideErrorMessage = () => {
    try {
      this.errorElement.classList.add('hidden');

      this.inputElement.removeAttribute('aria-invalid');
      this.inputElement.removeAttribute('aria-describedby');
    } catch (error) {
      console.error(error);
    }
  };

  updateHeaderHeight = () => {
    const announcementBar = document.getElementById(
      'shopify-section-announcement-bar'
    );
    const header = document.getElementById('shopify-section-header');
    const headerHeight = [announcementBar, header].reduce(
      (acc, cur) => (cur ? acc + cur.offsetHeight : acc),
      0
    );

    document.documentElement.style.setProperty(
      '--header-height',
      headerHeight + 'px'
    );
  };

  restrictFocus = (event) => {
    try {
      if (this.isOpen && !this.modalElement.contains(event.target)) {
        event.stopPropagation();
        this.modalElement.focus();
      }
    } catch (error) {
      console.error(error);
    }
  };

  setVariantId = (variantId) => (this.requestData.variant = variantId);
}

customElements.define('back-in-stock', BackInStock);

const emailInput = document.querySelector('.back-in-stock__form-input');
const notifyMeSubmitBtn = document.querySelector('.back-in-stock__form-submit');

emailInput.addEventListener('input', () => {
  if (emailInput.validity.valid) {
    notifyMeSubmitBtn.disabled = false;
  } else {
    notifyMeSubmitBtn.disabled = true;
  }
});
